import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaTag, FaPercent, FaCalendar, FaStar, FaEye, FaEyeSlash, FaSave, FaTimes, FaCheck, FaExclamationTriangle, FaSync, FaCopy, FaSort, FaEye as FaPreview } from 'react-icons/fa';
import { getCategoryDisplayName, getCategoryColorClasses } from '../../utils/planUtils';
import { getAllPlans, getHomepagePlans, getPlanStats, updatePlanHomepageVisibility, addPlan, updatePlan, deletePlan, forceRefreshPlans } from '../../homepage/homepagePlans';
import API_ENDPOINTS from '../../config/api';
import FeatureEditor from './FeatureEditor';
import PromoPricingManager from './PromoPricingManager';

/** Dark form controls: preflight makes inputs inherit parent text color (light on dark), while UA paints a light field — unreadable without explicit bg/text. */
const ADMIN_INPUT =
  'w-full p-3 rounded-lg border border-[#504945] bg-[#3c3836] text-[#fbf1c7] placeholder:text-[#928374] focus:ring-2 focus:ring-[#83a598] focus:border-[#83a598] focus:outline-none';

const PlanManagement = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [homepagePlans, setHomepagePlans] = useState([]);
  const [stats, setStats] = useState({});

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    speed: '',
    // Typical busy-hour (7–11pm) download + upload disclosure shown on the
    // marketplace plan cards & CIS (ACMA Broadband Speed Claims). Free-text,
    // e.g. "100 Mbps" / "20 Mbps", or "Confirmed once connected". Leave blank to
    // let the site derive from the NBN tier. Single source of truth — edit here,
    // it reflects on homepage cards + "Choose Your Plan" modal everywhere.
    typicalEveningSpeed: '',
    typicalEveningUpload: '',
    price: '',
    description: '',
    features: [],
    category: 'standard',
    isActive: true,
    isPopular: false,
    isNew: false,
    showOnHomepage: true,
    sortOrder: '0',
    data: 'Unlimited',
    color: 'from-blue-500 to-blue-600',
    // NBN access tech eligibility — controls which addresses see this plan after Vocus QUALIFY.
    // Empty array means "no eligibility filter" (legacy default).
    eligibleConnections: [],
    // Slug used by the marketplace to deep-link to this plan's row on /cis.
    // Convention: kebab-cased plan name (e.g. "Fast 500" → "fast-500").
    cisAnchor: '',
    // LineSpeed string from Vocus PlanID matrix (vocusBridge.js VOCUS_PLAN_MATRIX).
    // Drives which Vocus PlanID we send on FIBRE Create. Must match a key in the
    // matrix or auto-provisioning will throw "speedTier not available". Allowed
    // values: 25/10, 50/20, 100/40, Home Fast, Home Super Fast, 250/100,
    // 500/200, 1000/400, Home Ultra Fast, 500/50 (Home Fast).
    vocusSpeedTier: '',
    // New fields
    promotion: [],
    pricing: {
      setupFee: 0,
      contractMonths: 0,
      earlyTerminationFee: 0,
      priceIncreaseDate: null,
      priceIncreaseAmount: null
    },
    terms: {
      minimumTerm: 0,
      noticePeriod: 30,
      dataCap: 'Unlimited',
      fairUsePolicy: 'Yes',
      speedGuarantee: 'No',
      includedServices: [],
      excludedServices: []
    }
  });

  const [promoData, setPromoData] = useState({
    discountType: 'percentage',
    discountValue: '',
    startDate: '',
    endDate: '',
    promoCode: '',
    description: '',
    isActive: true
  });

  useEffect(() => {
    loadPlans();
    loadHomepagePlans();
    loadStats();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      
      // Try to load from DynamoDB first
      try {
        const response = await fetch(API_ENDPOINTS.PLAN_MANAGEMENT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`,
          },
          body: JSON.stringify({
            action: 'getAll'
          })
        });

        if (response.ok) {
          const result = await response.json();
          if (result.plans && result.plans.length > 0) {
            // Convert DynamoDB format to frontend format
            const formattedPlans = result.plans.map(plan => {
              const planIdValue = plan.planID || plan.planId || plan.id || `plan-${Date.now()}`;
              const planNameValue = plan.planName || plan.name || 'Untitled Plan';

              return {
                id: planIdValue,
                name: planNameValue,
                speed: plan.speed || '',
                typicalEveningSpeed: plan.typicalEveningSpeed || '',
                typicalEveningUpload: plan.typicalEveningUpload || '',
                price: plan.price,
                originalPrice: plan.originalPrice || plan.price,
                description: plan.description || '',
                features: plan.features || [],
                category: plan.category || 'standard',
                isActive: plan.isActive !== undefined ? plan.isActive : true,
                isPopular: plan.isPopular || false,
                isNew: plan.isNew || false,
                showOnHomepage: plan.showOnHomepage !== undefined ? plan.showOnHomepage : true,
                sortOrder: plan.sortOrder || 0,
                data: plan.data || 'Unlimited',
                color: plan.color || 'from-blue-500 to-blue-600',
                // Enhanced fields
                promotion: plan.promotion || null,
                pricing: plan.pricing || null,
                terms: plan.terms || null,
                // Backward compatibility
                hasPromo: plan.hasPromo || (plan.promotion && Array.isArray(plan.promotion) && plan.promotion.some(p => p.isActive)) || false,
                promoCode: plan.promoCode || (plan.promotion && Array.isArray(plan.promotion) && plan.promotion[0] ? plan.promotion[0].promoCode : null) || null,
                discountValue: plan.discountValue || (plan.promotion && Array.isArray(plan.promotion) && plan.promotion[0] ? plan.promotion[0].discountValue : null) || null
              };
            });
            setPlans(formattedPlans);
            console.log('Loaded plans from DynamoDB:', formattedPlans);
            return;
          }
        }
      } catch (apiError) {
        console.warn('Failed to load plans from DynamoDB, using local storage:', apiError);
      }
      
      // Fallback to local storage
      const allPlans = getAllPlans();
      console.log('Loaded plans from local storage:', allPlans);
      setPlans(allPlans);
    } catch (error) {
      console.error('Error loading plans:', error);
      setError('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const loadHomepagePlans = () => {
    try {
      const homepagePlansData = getHomepagePlans();
      console.log('Loaded homepage plans:', homepagePlansData);
      setHomepagePlans(homepagePlansData);
    } catch (error) {
      console.error('Error loading homepage plans:', error);
      setHomepagePlans([]);
    }
  };

  const loadStats = () => {
    try {
      const planStats = getPlanStats();
      console.log('Loaded plan stats:', planStats);
      setStats(planStats);
    } catch (error) {
      console.error('Error loading plan stats:', error);
      setStats({ total: 0, onHomepage: 0, popular: 0, new: 0, promotional: 0 });
    }
  };

  const handleRefreshHomepage = () => {
    // Force refresh plans data
    forceRefreshPlans();
    
    // Reload all data
    loadPlans();
    loadHomepagePlans();
    loadStats();
    
    // Dispatch custom event to refresh homepage plans
    window.dispatchEvent(new CustomEvent('refreshHomepagePlans'));
    setSuccess('Homepage plans refreshed! Super Deal plan should now be visible.');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleAddPlan = () => {
    setFormData({
      name: '',
      speed: '',
      typicalEveningSpeed: '',
      typicalEveningUpload: '',
      price: '',
      description: '',
      features: [],
      category: 'standard',
      isActive: true,
      isPopular: false,
      isNew: false,
      showOnHomepage: true,
      sortOrder: String(plans.length + 1),
      data: 'Unlimited',
      color: 'from-blue-500 to-blue-600',
      eligibleConnections: [],
      cisAnchor: '',
      vocusSpeedTier: '',
      // New fields initialized
      promotion: [],
      pricing: {
        setupFee: 0,
        contractMonths: 0,
        earlyTerminationFee: 0,
        priceIncreaseDate: null,
        priceIncreaseAmount: null
      },
      terms: {
        minimumTerm: 0,
        noticePeriod: 30,
        dataCap: 'Unlimited',
        fairUsePolicy: 'Yes',
        speedGuarantee: 'No',
        includedServices: [],
        excludedServices: []
      }
    });
    setShowAddModal(true);
    setError('');
  };

  const handleEditPlan = (plan) => {
    setSelectedPlan(plan);
    
    // Normalize features - handle both string and object formats
    const normalizedFeatures = (plan.features || []).map((f, index) => {
      if (typeof f === 'string') {
        return {
          id: `feature-${index}`,
          title: f.trim(),
          description: '',
          icon: null,
          category: null,
          order: index
        };
      }
      return {
        id: f.id || `feature-${index}`,
        title: f.title || '',
        description: f.description || '',
        icon: f.icon || null,
        category: f.category || null,
        order: f.order !== undefined ? f.order : index
      };
    });
    
    setFormData({
      name: plan.name,
      speed: plan.speed,
      typicalEveningSpeed: plan.typicalEveningSpeed || '',
      typicalEveningUpload: plan.typicalEveningUpload || '',
      price: plan.price !== undefined && plan.price !== null ? plan.price.toString() : '',
      description: plan.description || '',
      features: normalizedFeatures,
      category: plan.category || 'standard',
      isActive: plan.isActive !== undefined ? plan.isActive : true,
      isPopular: plan.isPopular || false,
      isNew: plan.isNew || false,
      showOnHomepage: plan.showOnHomepage !== undefined ? plan.showOnHomepage : true,
      sortOrder: plan.sortOrder !== undefined && plan.sortOrder !== null ? plan.sortOrder.toString() : '0',
      data: plan.data || 'Unlimited',
      color: plan.color || 'from-blue-500 to-blue-600',
      eligibleConnections: Array.isArray(plan.eligibleConnections) ? plan.eligibleConnections : [],
      cisAnchor: plan.cisAnchor || '',
      vocusSpeedTier: plan.vocusSpeedTier || '',
      // Enhanced fields
      promotion: plan.promotion || (plan.hasPromo ? [{
        isActive: plan.hasPromo,
        discountType: 'percentage',
        discountValue: plan.discountValue || 0,
        promoCode: plan.promoCode || '',
        originalPrice: plan.price || 0
      }] : []),
      pricing: plan.pricing || {
        setupFee: 0,
        contractMonths: 0,
        earlyTerminationFee: 0,
        priceIncreaseDate: null,
        priceIncreaseAmount: null
      },
      terms: plan.terms || {
        minimumTerm: 0,
        noticePeriod: 30,
        dataCap: plan.data || 'Unlimited',
        fairUsePolicy: 'Yes',
        speedGuarantee: 'No',
        includedServices: [],
        excludedServices: []
      }
    });
    setShowEditModal(true);
    setError('');
  };

  const handlePromoPlan = (plan) => {
    setSelectedPlan(plan);
    setPromoData({
      discountType: 'percentage',
      discountValue: '',
      startDate: '',
      endDate: '',
      promoCode: '',
      description: '',
      isActive: true
    });
    setShowPromoModal(true);
    setError('');
  };

  const handleToggleHomepage = async (planId) => {
    try {
      const plan = plans.find(p => p.id === planId);
      const newVisibility = !plan.showOnHomepage;
      
      // Update the plan in our local state
      const updatedPlan = {
        ...plan,
        showOnHomepage: newVisibility
      };
      
      setPlans(plans.map(p => p.id === planId ? updatedPlan : p));
      
      // Update the global plan data
      updatePlanHomepageVisibility(planId, newVisibility);
      
      // Reload homepage plans
      loadHomepagePlans();
      
      setSuccess(`Plan ${newVisibility ? 'added to' : 'removed from'} homepage successfully!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to toggle homepage visibility');
    }
  };

  const handleSavePlan = async (isEdit = false) => {
    try {
      // Validation
      if (!formData.name.trim()) {
        setError('Plan name is required');
        return;
      }
      if (!formData.speed.trim()) {
        setError('Speed is required');
        return;
      }
      if (!formData.price || formData.price <= 0) {
        setError('Valid price is required');
        return;
      }
      // Validate features - check if any feature has a title
      const hasValidFeatures = formData.features && formData.features.length > 0 && 
        formData.features.some(f => {
          if (typeof f === 'string') return f.trim() !== '';
          return f.title && f.title.trim() !== '';
        });
      
      if (!hasValidFeatures) {
        setError('At least one feature with a title is required');
        return;
      }
      
      // Check for duplicate plan names (except when editing the same plan)
      const existingPlan = plans.find(p => 
        p.name.toLowerCase() === formData.name.trim().toLowerCase() && 
        (!isEdit || p.id !== selectedPlan.id)
      );
      if (existingPlan) {
        setError('A plan with this name already exists');
        return;
      }

      // Create new plan object
      const planPrimaryId = isEdit && selectedPlan ? selectedPlan.id : `plan-${Date.now()}`;

      // Normalize features for saving
      const normalizedFeaturesForSave = formData.features
        .filter(f => {
          if (typeof f === 'string') return f.trim() !== '';
          return f.title && f.title.trim() !== '';
        })
        .map(f => {
          if (typeof f === 'string') return f.trim();
          return {
            id: f.id || `feature-${Date.now()}-${Math.random()}`,
            title: f.title.trim(),
            description: f.description || '',
            icon: f.icon || null,
            category: f.category || null,
            order: typeof f.order === 'number' ? f.order : 0
          };
        });

      const newPlan = {
        id: planPrimaryId,
        name: formData.name.trim(),
        speed: formData.speed.trim(),
        typicalEveningSpeed: (formData.typicalEveningSpeed || '').trim() || undefined,
        typicalEveningUpload: (formData.typicalEveningUpload || '').trim() || undefined,
        price: parseFloat(formData.price),
        originalPrice: parseFloat(formData.price),
        description: formData.description.trim(),
        features: normalizedFeaturesForSave,
        category: formData.category,
        isActive: formData.isActive,
        isPopular: formData.isPopular,
        isNew: formData.isNew,
        showOnHomepage: formData.showOnHomepage,
        sortOrder: formData.sortOrder === '' ? 0 : parseInt(formData.sortOrder, 10) || 0,
        data: formData.data,
        color: formData.color,
        eligibleConnections: Array.isArray(formData.eligibleConnections) ? formData.eligibleConnections : [],
        cisAnchor: (formData.cisAnchor || '').trim() || undefined,
        vocusSpeedTier: (formData.vocusSpeedTier || '').trim() || undefined,
        // Enhanced fields
        promotion: formData.promotion && formData.promotion.length > 0 ? formData.promotion : undefined,
        pricing: formData.pricing,
        terms: formData.terms,
        // Backward compatibility
        hasPromo: formData.promotion && formData.promotion.length > 0 && formData.promotion.some(p => p.isActive),
        promoCode: formData.promotion && formData.promotion[0] ? formData.promotion[0].promoCode : null,
        discountValue: formData.promotion && formData.promotion[0] ? formData.promotion[0].discountValue : null
      };

      // Save to DynamoDB via API
      const apiAction = isEdit ? 'update' : 'create';
      const response = await fetch(API_ENDPOINTS.PLAN_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`,
        },
        body: JSON.stringify({
          action: apiAction,
          planId: planPrimaryId,
          planID: planPrimaryId,
          planName: newPlan.name,
          plan: {
            ...newPlan,
            planName: newPlan.name // DynamoDB uses planName field
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save plan');
      }

      const result = await response.json();

      // Also update local storage for frontend compatibility
      if (isEdit) {
        updatePlan(selectedPlan.id, newPlan);
        setPlans(plans.map(p => p.id === selectedPlan.id ? newPlan : p));
        setSuccess('Plan updated successfully in DynamoDB!');
      } else {
        addPlan(newPlan);
        setPlans([...plans, newPlan]);
        setSuccess('Plan added successfully to DynamoDB!');
      }

      // Close modal and reload data
      setShowAddModal(false);
      setShowEditModal(false);
      loadHomepagePlans();
      loadStats();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving plan:', error);
      setError(`Failed to save plan: ${error.message}`);
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan? This action cannot be undone.')) {
      return;
    }

    try {
      // Delete from DynamoDB via API
      const plan = plans.find(p => p.id === planId);
      const response = await fetch(API_ENDPOINTS.PLAN_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}`,
        },
        body: JSON.stringify({
          action: 'delete',
          planID: planId,
          planId: planId,
          planName: plan?.name
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete plan');
      }

      // Also update local storage for frontend compatibility
      deletePlan(planId);
      setPlans(plans.filter(p => p.id !== planId));
      setSuccess('Plan deleted successfully from DynamoDB!');
      loadHomepagePlans();
      loadStats();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting plan:', error);
      setError(`Failed to delete plan: ${error.message}`);
    }
  };


  const copyPlanId = (planId) => {
    navigator.clipboard.writeText(planId);
    setSuccess('Plan ID copied to clipboard!');
    setTimeout(() => setSuccess(''), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-lg">Loading plans...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">⚠️ Error Loading Plans</div>
          <div className="text-[#bdae93] mb-4">{error}</div>
          <button 
            onClick={() => {
              setError('');
              loadPlans();
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-[#ebdbb2]">Plan Management</h2>
          <p className="text-[#bdae93]">Manage internet plans and homepage display</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleRefreshHomepage}
            className="bg-[#b8bb26] text-[#1d2021] px-4 py-3 rounded-lg font-semibold hover:bg-[#98971a] transition-colors flex items-center"
          >
            <FaSync className="mr-2" />
            Refresh Homepage
          </button>
          <button
            onClick={() => {
              forceRefreshPlans();
              loadPlans();
              loadHomepagePlans();
              loadStats();
              setSuccess('Plans data force refreshed! Super Deal plan should now be visible.');
              setTimeout(() => setSuccess(''), 3000);
            }}
            className="bg-[#fabd2f] text-[#1d2021] px-4 py-3 rounded-lg font-semibold hover:bg-[#fe8019] transition-colors flex items-center"
          >
            <FaSync className="mr-2" />
            Force Refresh Plans
          </button>
          <button
            onClick={handleAddPlan}
            className="bg-[#83a598] text-[#1d2021] px-6 py-3 rounded-lg font-semibold hover:bg-[#458588] transition-colors flex items-center"
          >
            <FaPlus className="mr-2" />
            Add New Plan
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-[#282828] p-4 rounded-lg shadow border border-[#3c3836]">
          <div className="flex items-center">
            <div className="p-2 bg-[#3c3836] rounded-lg">
              <FaTag className="text-[#83a598]" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-[#bdae93]">Total Plans</p>
              <p className="text-lg font-semibold text-[#ebdbb2]">{stats.total || plans.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-[#282828] p-4 rounded-lg shadow border border-[#3c3836]">
          <div className="flex items-center">
            <div className="p-2 bg-[#3c3836] rounded-lg">
              <FaEye className="text-[#b8bb26]" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-[#bdae93]">On Homepage</p>
              <p className="text-lg font-semibold text-[#ebdbb2]">{stats.onHomepage || plans.filter(p => p.showOnHomepage).length}</p>
            </div>
          </div>
        </div>
        <div className="bg-[#282828] p-4 rounded-lg shadow border border-[#3c3836]">
          <div className="flex items-center">
            <div className="p-2 bg-[#3c3836] rounded-lg">
              <FaStar className="text-[#fabd2f]" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-[#bdae93]">Popular Plans</p>
              <p className="text-lg font-semibold text-[#ebdbb2]">{stats.popular || plans.filter(p => p.isPopular).length}</p>
            </div>
          </div>
        </div>
        <div className="bg-[#282828] p-4 rounded-lg shadow border border-[#3c3836]">
          <div className="flex items-center">
            <div className="p-2 bg-[#3c3836] rounded-lg">
              <FaPercent className="text-[#d3869b]" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-[#bdae93]">Promo Plans</p>
              <p className="text-lg font-semibold text-[#ebdbb2]">{stats.promotional || plans.filter(p => p.hasPromo).length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center text-green-700">
            <FaCheck className="mr-2" />
            <span>{success}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center text-red-700">
            <FaExclamationTriangle className="mr-2" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Plans Table */}
      <div className="bg-[#282828] rounded-lg shadow overflow-hidden border border-[#3c3836]">
        <div className="px-6 py-4 border-b border-[#3c3836]">
          <h3 className="text-lg font-semibold text-[#ebdbb2]">All Plans</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#3c3836]">
            <thead className="bg-[#3c3836]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#bdae93] uppercase tracking-wider">
                  Plan Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#bdae93] uppercase tracking-wider">
                  Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#bdae93] uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#bdae93] uppercase tracking-wider">
                  Homepage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#bdae93] uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#282828] divide-y divide-[#3c3836]">
              {plans.map((plan) => (
                <tr key={plan.id} className="hover:bg-[#3c3836]">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded-full bg-gradient-to-r ${plan.color}`}></div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-[#ebdbb2]">{plan.name}</div>
                        <div className="text-sm text-[#bdae93]">{plan.speed}</div>
                        <div className="text-xs text-[#928374]">ID: {plan.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-[#ebdbb2]">${plan.price}</div>
                    {plan.hasPromo && (
                      <div className="text-xs text-[#b8bb26]">Promo: {plan.promoCode}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        plan.isActive ? 'bg-[#3c3836] text-[#b8bb26] border border-[#458588]' : 'bg-[#3c3836] text-[#fb4934] border border-[#cc241d]'
                      }`}>
                        {plan.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {plan.isPopular && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#3c3836] text-[#fabd2f] border border-[#d79921]">
                          Popular
                        </span>
                      )}
                      {plan.isNew && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#3c3836] text-[#83a598] border border-[#458588]">
                          New
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleHomepage(plan.id)}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        plan.showOnHomepage
                          ? 'bg-[#3c3836] text-[#b8bb26] hover:bg-[#504945] border border-[#458588]'
                          : 'bg-[#3c3836] text-[#928374] hover:bg-[#504945] border border-[#504945]'
                      }`}
                    >
                      {plan.showOnHomepage ? <FaEye className="mr-1" /> : <FaEyeSlash className="mr-1" />}
                      {plan.showOnHomepage ? 'Visible' : 'Hidden'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => copyPlanId(plan.id)}
                        className="text-[#928374] hover:text-[#bdae93]"
                        title="Copy Plan ID"
                      >
                        <FaCopy />
                      </button>
                      <button
                        onClick={() => handleEditPlan(plan)}
                        className="text-[#83a598] hover:text-[#458588]"
                        title="Edit Plan"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handlePromoPlan(plan)}
                        className="text-[#d3869b] hover:text-[#b16286]"
                        title="Add Promotion"
                      >
                        <FaPercent />
                      </button>
                      <button
                        onClick={() => handleDeletePlan(plan.id)}
                        className="text-[#fb4934] hover:text-[#cc241d]"
                        title="Delete Plan"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Featured Plans Preview */}
      <div className="bg-[#282828] rounded-lg shadow border border-[#3c3836]">
        <div className="px-6 py-4 border-b border-[#3c3836]">
          <h3 className="text-lg font-semibold text-[#ebdbb2]">Featured Plans Preview (Homepage)</h3>
          <p className="text-sm text-[#bdae93]">These are the 3 plans currently displayed on the homepage</p>
        </div>
        <div className="p-6">
          {homepagePlans.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {homepagePlans.map((plan, index) => (
                <div key={plan.id} className="border border-[#3c3836] rounded-lg p-4 bg-[#3c3836]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-[#928374]">#{index + 1}</span>
                    {plan.popular && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[#282828] text-[#fabd2f] border border-[#d79921]">
                        Popular
                      </span>
                    )}
                  </div>
                  <h4 className="font-semibold text-[#ebdbb2]">{plan.name}</h4>
                  <p className="text-sm text-[#bdae93]">{plan.speed}</p>
                  <p className="text-lg font-bold text-[#83a598]">${plan.price}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-[#928374]">
              <FaExclamationTriangle className="mx-auto h-12 w-12 text-[#928374]" />
              <h3 className="mt-2 text-sm font-medium text-[#ebdbb2]">No plans on homepage</h3>
              <p className="mt-1 text-sm text-[#bdae93]">Add plans to homepage visibility to see them here.</p>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Plan Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-[#282828] rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-[#3c3836] [color-scheme:dark]">
            <div className="p-6 border-b border-[#3c3836]">
              <h3 className="text-xl font-bold text-[#ebdbb2]">
                {showAddModal ? 'Add New Plan' : 'Edit Plan'}
              </h3>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#bdae93] mb-2">Plan Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className={ADMIN_INPUT}
                    placeholder="e.g., Premium Fibre 150 Mbps"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#bdae93] mb-2">Speed *</label>
                  <input
                    type="text"
                    value={formData.speed}
                    onChange={(e) => setFormData({...formData, speed: e.target.value})}
                    className={ADMIN_INPUT}
                    placeholder="e.g., 150 Mbps"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#bdae93] mb-2">
                  Typical Evening Speed (7&ndash;11pm)
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={formData.typicalEveningSpeed}
                    onChange={(e) => setFormData({ ...formData, typicalEveningSpeed: e.target.value })}
                    className={ADMIN_INPUT}
                    placeholder="Download e.g. 100 Mbps (blank = auto)"
                  />
                  <input
                    type="text"
                    value={formData.typicalEveningUpload}
                    onChange={(e) => setFormData({ ...formData, typicalEveningUpload: e.target.value })}
                    className={ADMIN_INPUT}
                    placeholder="Upload e.g. 20 Mbps (blank = auto)"
                  />
                </div>
                <p className="mt-1 text-xs text-[#7c6f64]">
                  ACMA busy-hour disclosure shown on the marketplace plan cards &amp; CIS. Leave blank
                  and the site derives it from the tier (download = headline tier, upload = the plan&apos;s
                  /upload figure). Set a value here to override with your own measured numbers.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#bdae93] mb-2">Price ($) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    className={ADMIN_INPUT}
                    placeholder="89.99"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#bdae93] mb-2">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className={ADMIN_INPUT}
                  >
                    <option value="basic">Basic</option>
                    <option value="standard">Standard</option>
                    <option value="premium">Premium</option>
                    <option value="ultra">Ultra</option>
                    <option value="gigabit">Gigabit</option>
                    <option value="super">Super</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#bdae93] mb-2">Sort Order</label>
                  <input
                    type="number"
                    value={formData.sortOrder}
                    onChange={(e) => setFormData({
                      ...formData,
                      sortOrder: e.target.value
                    })}
                    className={ADMIN_INPUT}
                    placeholder="1"
                  />
                </div>
              </div>

              {/* NBN access-tech eligibility — drives which addresses see this plan after Vocus QUALIFY */}
              <div>
                <label className="block text-sm font-medium text-[#bdae93] mb-2">
                  Eligible NBN Connection Types
                  <span className="text-xs text-[#a89984] ml-2">
                    (which Vocus access technologies this plan is offered for; leave none selected to allow all)
                  </span>
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 p-3 bg-[#1d2021] border border-[#3c3836] rounded-lg">
                  {['FTTP', 'FTTB', 'FTTN', 'FTTC', 'HFC', 'FW'].map((tech) => {
                    const checked = (formData.eligibleConnections || []).includes(tech);
                    return (
                      <label
                        key={tech}
                        className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg cursor-pointer border text-sm transition ${
                          checked
                            ? 'bg-[#fabd2f]/20 border-[#fabd2f] text-[#fabd2f] font-semibold'
                            : 'bg-[#282828] border-[#3c3836] text-[#ebdbb2] hover:bg-[#3c3836]'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={checked}
                          onChange={(e) => {
                            const list = new Set(formData.eligibleConnections || []);
                            if (e.target.checked) list.add(tech);
                            else list.delete(tech);
                            setFormData({ ...formData, eligibleConnections: Array.from(list) });
                          }}
                        />
                        {tech}
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-[#a89984] mt-2">
                  e.g. a 1000 Mbps plan should only target <span className="text-[#fabd2f]">FTTP</span> + <span className="text-[#fabd2f]">HFC</span>; a 100 Mbps plan can include <span className="text-[#fabd2f]">FTTN</span>, <span className="text-[#fabd2f]">FTTB</span>, <span className="text-[#fabd2f]">FW</span>.
                </p>
              </div>

              {/* Per-plan CIS anchor + Vocus PlanID-matrix speed tier.
                  These two fields used to require CLI edits — surfacing them here so admin
                  can manage them from the UI. */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#bdae93] mb-2">
                    CIS Anchor Slug
                    <span className="text-xs text-[#a89984] ml-2">
                      (deep-link target for /cis#plan-&lt;slug&gt;)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={formData.cisAnchor}
                    onChange={(e) => setFormData({ ...formData, cisAnchor: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-') })}
                    className={ADMIN_INPUT}
                    placeholder={formData.name ? formData.name.toLowerCase().replace(/\s+/g, '-') : 'e.g. fast-500'}
                  />
                  <p className="text-xs text-[#a89984] mt-1">
                    Auto-suggest: <span className="text-[#fabd2f]">{formData.name ? formData.name.toLowerCase().replace(/\s+/g, '-') : '(set plan name first)'}</span>
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#bdae93] mb-2">
                    Vocus Speed Tier
                    <span className="text-xs text-[#a89984] ml-2">
                      (PlanID matrix key — drives FIBRE Create)
                    </span>
                  </label>
                  <select
                    value={formData.vocusSpeedTier}
                    onChange={(e) => setFormData({ ...formData, vocusSpeedTier: e.target.value })}
                    className={ADMIN_INPUT}
                  >
                    <option value="">— Not set (manual provisioning only) —</option>
                    <option value="25/10">25/10 (Starter)</option>
                    <option value="50/20">50/20 (Basic)</option>
                    <option value="100/40">100/40 (Standard premium upload)</option>
                    <option value="Home Fast">Home Fast (100/20)</option>
                    <option value="250/100">250/100 (FTTP only)</option>
                    <option value="500/50 (Home Fast)">500/50 (Home Fast) — FTTP/HFC, Sep-2025 launch</option>
                    <option value="500/200">500/200 (FTTP only)</option>
                    <option value="Home Super Fast">Home Super Fast (250/25 or 750/50)</option>
                    <option value="Home Ultra Fast">Home Ultra Fast (500-1000/50)</option>
                    <option value="1000/400">1000/400 (FTTP only)</option>
                  </select>
                  <p className="text-xs text-[#a89984] mt-1">
                    Must match a key in <code className="text-[#fabd2f]">vocusBridge.js VOCUS_PLAN_MATRIX</code> or auto-provisioning will throw.
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#bdae93] mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows="3"
                  className={ADMIN_INPUT}
                  placeholder="Plan description..."
                />
              </div>

              <div>
                <FeatureEditor
                  features={formData.features}
                  onChange={(features) => setFormData({...formData, features})}
                  error={formData.features.length === 0 ? 'At least one feature is required' : ''}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#bdae93] mb-2">Color Scheme</label>
                  <select
                    value={formData.color}
                    onChange={(e) => setFormData({...formData, color: e.target.value})}
                    className={ADMIN_INPUT}
                  >
                    <option value="from-blue-500 to-blue-600">Blue</option>
                    <option value="from-green-500 to-green-600">Green</option>
                    <option value="from-purple-500 to-purple-600">Purple</option>
                    <option value="from-yellow-500 to-yellow-600">Yellow</option>
                    <option value="from-red-500 to-red-600">Red</option>
                    <option value="from-[#FFD700] to-[#FFB700]">Gold</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#bdae93] mb-2">Data Allowance</label>
                  <input
                    type="text"
                    value={formData.data}
                    onChange={(e) => setFormData({...formData, data: e.target.value})}
                    className={ADMIN_INPUT}
                    placeholder="Unlimited"
                  />
                </div>
              </div>

              {/* Promotional Pricing Section */}
              <div className="border-t border-[#3c3836] pt-4">
                <PromoPricingManager
                  basePrice={parseFloat(formData.price) || 0}
                  promotions={formData.promotion}
                  onChange={(promotions) => setFormData({...formData, promotion: promotions})}
                  error=""
                />
              </div>

              {/* Pricing Details Section */}
              <div className="border-t border-[#3c3836] pt-4">
                <h4 className="text-sm font-semibold text-[#ebdbb2] mb-3">Pricing Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#bdae93] mb-2">Setup Fee ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.pricing?.setupFee || 0}
                      onChange={(e) => setFormData({
                        ...formData,
                        pricing: {...formData.pricing, setupFee: parseFloat(e.target.value) || 0}
                      })}
                      className={ADMIN_INPUT}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#bdae93] mb-2">Contract Length (months)</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.pricing?.contractMonths || 0}
                      onChange={(e) => setFormData({
                        ...formData,
                        pricing: {...formData.pricing, contractMonths: parseInt(e.target.value) || 0}
                      })}
                      className={ADMIN_INPUT}
                      placeholder="0 = no contract"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#bdae93] mb-2">Early Termination Fee ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.pricing?.earlyTerminationFee || 0}
                      onChange={(e) => setFormData({
                        ...formData,
                        pricing: {...formData.pricing, earlyTerminationFee: parseFloat(e.target.value) || 0}
                      })}
                      className={ADMIN_INPUT}
                    />
                  </div>
                </div>
              </div>

              {/* Terms & Conditions Section */}
              <div className="border-t border-[#3c3836] pt-4">
                <h4 className="text-sm font-semibold text-[#ebdbb2] mb-3">Terms & Conditions</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#bdae93] mb-2">Minimum Term (months)</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.terms?.minimumTerm || 0}
                      onChange={(e) => setFormData({
                        ...formData,
                        terms: {...formData.terms, minimumTerm: parseInt(e.target.value) || 0}
                      })}
                      className={ADMIN_INPUT}
                      placeholder="0 = month-to-month"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#bdae93] mb-2">Notice Period (days)</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.terms?.noticePeriod || 30}
                      onChange={(e) => setFormData({
                        ...formData,
                        terms: {...formData.terms, noticePeriod: parseInt(e.target.value) || 30}
                      })}
                      className={ADMIN_INPUT}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#bdae93] mb-2">Speed Guarantee</label>
                    <select
                      value={formData.terms?.speedGuarantee || 'No'}
                      onChange={(e) => setFormData({
                        ...formData,
                        terms: {...formData.terms, speedGuarantee: e.target.value}
                      })}
                      className={ADMIN_INPUT}
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#bdae93] mb-2">Fair Use Policy</label>
                    <select
                      value={formData.terms?.fairUsePolicy || 'Yes'}
                      onChange={(e) => setFormData({
                        ...formData,
                        terms: {...formData.terms, fairUsePolicy: e.target.value}
                      })}
                      className={ADMIN_INPUT}
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-[#504945] rounded"
                  />
                  <span className="text-sm font-medium text-[#bdae93]">Active Plan</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isPopular}
                    onChange={(e) => setFormData({...formData, isPopular: e.target.checked})}
                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-[#504945] rounded"
                  />
                  <span className="text-sm font-medium text-[#bdae93]">Popular Plan</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isNew}
                    onChange={(e) => setFormData({...formData, isNew: e.target.checked})}
                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-[#504945] rounded"
                  />
                  <span className="text-sm font-medium text-[#bdae93]">New Plan</span>
                </label>
              </div>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.showOnHomepage}
                  onChange={(e) => setFormData({...formData, showOnHomepage: e.target.checked})}
                  className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-[#504945] rounded"
                />
                <span className="text-sm font-medium text-[#bdae93]">Show on Homepage</span>
              </label>
            </div>

            <div className="p-6 border-t border-[#3c3836] flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                }}
                className="px-4 py-2 border border-[#504945] rounded-lg text-[#bdae93] hover:bg-[#3c3836]"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSavePlan(showEditModal)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
              >
                <FaSave className="mr-2" />
                {showEditModal ? 'Update Plan' : 'Create Plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Promo Modal */}
      {showPromoModal && selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-[#282828] rounded-xl max-w-md w-full border border-[#3c3836] [color-scheme:dark]">
            <div className="p-6 border-b border-[#3c3836]">
              <h3 className="text-xl font-bold text-[#ebdbb2]">Add Promotion</h3>
              <p className="text-sm text-[#bdae93]">Add promotional pricing to {selectedPlan.name}</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#bdae93] mb-2">Promo Code</label>
                <input
                  type="text"
                  value={promoData.promoCode}
                  onChange={(e) => setPromoData({...promoData, promoCode: e.target.value})}
                  className={ADMIN_INPUT}
                  placeholder="SAVE10"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#bdae93] mb-2">Discount Type</label>
                  <select
                    value={promoData.discountType}
                    onChange={(e) => setPromoData({...promoData, discountType: e.target.value})}
                    className={ADMIN_INPUT}
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed Amount</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#bdae93] mb-2">Discount Value</label>
                  <input
                    type="number"
                    step="0.01"
                    value={promoData.discountValue}
                    onChange={(e) => setPromoData({...promoData, discountValue: e.target.value})}
                    className={ADMIN_INPUT}
                    placeholder={promoData.discountType === 'percentage' ? '10' : '10.00'}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#bdae93] mb-2">Start Date</label>
                  <input
                    type="date"
                    value={promoData.startDate}
                    onChange={(e) => setPromoData({...promoData, startDate: e.target.value})}
                    className={ADMIN_INPUT}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#bdae93] mb-2">End Date</label>
                  <input
                    type="date"
                    value={promoData.endDate}
                    onChange={(e) => setPromoData({...promoData, endDate: e.target.value})}
                    className={ADMIN_INPUT}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#bdae93] mb-2">Description</label>
                <textarea
                  value={promoData.description}
                  onChange={(e) => setPromoData({...promoData, description: e.target.value})}
                  rows="3"
                  className={ADMIN_INPUT}
                  placeholder="Promotion description..."
                />
              </div>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={promoData.isActive}
                  onChange={(e) => setPromoData({...promoData, isActive: e.target.checked})}
                  className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-[#504945] rounded"
                />
                <span className="text-sm font-medium text-[#bdae93]">Active Promotion</span>
              </label>
            </div>

            <div className="p-6 border-t border-[#3c3836] flex justify-end space-x-3">
              <button
                onClick={() => setShowPromoModal(false)}
                className="px-4 py-2 border border-[#504945] rounded-lg text-[#bdae93] hover:bg-[#3c3836]"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Handle promo save logic here
                  setShowPromoModal(false);
                  setSuccess('Promotion added successfully!');
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Add Promotion
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanManagement;
